import {
  ApiError,
  formatMoney,
  type InvoiceSummary,
  queryKeys,
  type Unit,
  VENDOR_RETURN_STATUSES,
  type VendorCredit,
  type VendorReturn,
  type VendorReturnStatus,
} from '@mevaser/shared'
import {
  Badge,
  type BadgeProps,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Field,
  humanizeStatus,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Textarea,
  useToast,
} from '@mevaser/ui'
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { ColumnDef } from '@tanstack/react-table'
import { PackageX, Plus } from 'lucide-react'
import { useMemo, useState } from 'react'
import { api } from '../lib/api.js'
import { DataTable } from './data-table.js'
import { useDebouncedValue } from './image-library-grid.js'

const RETURN_STATUS_TONE: Record<VendorReturnStatus, NonNullable<BadgeProps['tone']>> = {
  needs_return: 'warning',
  shipped: 'info',
  received_by_vendor: 'info',
  credited: 'success',
  cancelled: 'neutral',
}

const RETURN_STATUS_LABEL: Record<VendorReturnStatus, string> = {
  needs_return: 'Needs return',
  shipped: 'Shipped back',
  received_by_vendor: 'Received by vendor',
  credited: 'Refunded',
  cancelled: 'Cancelled',
}

export function ReturnStatusBadge({ status }: { status: VendorReturnStatus }) {
  return <Badge tone={RETURN_STATUS_TONE[status]}>{RETURN_STATUS_LABEL[status]}</Badge>
}

const dollarsToCents = (value: string) => Math.round(Number(value || 0) * 100)
const centsToDollars = (cents: number) => (cents / 100).toFixed(2)

function errorToast(toast: ReturnType<typeof useToast>, title: string) {
  return (error: Error) => toast.error(title, error instanceof ApiError ? error.message : undefined)
}

function useInvalidateReturns() {
  const queryClient = useQueryClient()
  return () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: queryKeys.vendorReturns.all() }),
      queryClient.invalidateQueries({ queryKey: queryKeys.purchasing.all() }),
      queryClient.invalidateQueries({ queryKey: queryKeys.units.all() }),
      queryClient.invalidateQueries({ queryKey: queryKeys.stock.all() }),
      queryClient.invalidateQueries({ queryKey: ['vendors', 'credit'] }),
    ])
}

// ------------------------------------------------------------ start a return

/** What the dialog starts from when opened off a specific unit or purchase line. */
export interface ReturnPrefill {
  unitId?: string | null
  imei?: string | null
  productId?: string | null
  productName?: string | null
  vendorName?: string | null
  invoiceRef?: string | null
  sourceCostId?: string | null
  amountCents?: number | null
}

interface VendorOption {
  id: string
  name: string
}

interface LocationOption {
  id: string
  name: string
  isActive: boolean
}

interface StockProduct {
  id: string
  name: string
  type: string
}

/**
 * "Needs to be returned": takes a device (by IMEI) or counted stock out of
 * stock and opens a return against a vendor. Opened blank from the Returns
 * tab, or prefilled from a purchase line / a unit's row menu.
 */
export function StartReturnDialog({
  open,
  prefill,
  onClose,
}: {
  open: boolean
  prefill: ReturnPrefill | null
  onClose: () => void
}) {
  const toast = useToast()
  const invalidate = useInvalidateReturns()
  const [mode, setMode] = useState<'device' | 'stock'>('device')
  const [imei, setImei] = useState('')
  const [productId, setProductId] = useState('')
  const [locationId, setLocationId] = useState('')
  const [qty, setQty] = useState('1')
  const [vendorId, setVendorId] = useState('')
  const [reason, setReason] = useState('')
  const [amount, setAmount] = useState('')
  const [invoiceRef, setInvoiceRef] = useState('')

  const prefilledUnit = prefill?.unitId ?? null
  // A prefilled line decides what is being returned; a blank dialog lets staff choose.
  const kind: 'device' | 'stock' = prefilledUnit ? 'device' : prefill?.productId ? 'stock' : mode
  const lookup = useDebouncedValue(imei.trim(), 300)

  const { data: vendors } = useQuery({
    queryKey: queryKeys.vendors.list({ isActive: true, pageSize: 200 }),
    queryFn: () =>
      api.get<{ items: VendorOption[] }>('/vendors', { query: { isActive: true, pageSize: 200 } }),
    enabled: open,
  })
  const { data: locations } = useQuery({
    queryKey: queryKeys.locations(),
    queryFn: () => api.get<LocationOption[]>('/locations'),
    enabled: open && kind === 'stock',
  })
  const { data: stockProducts } = useQuery({
    queryKey: queryKeys.products.list({ type: 'stock', isActive: true }),
    queryFn: () =>
      api.get<{ items: StockProduct[] }>('/products', {
        query: { type: 'stock', isActive: true, pageSize: 200 },
      }),
    enabled: open && kind === 'stock' && !prefill?.productId,
  })
  const { data: found } = useQuery({
    queryKey: queryKeys.units.list({ q: lookup, forReturn: true }),
    queryFn: () => api.get<{ items: Unit[] }>('/units', { query: { q: lookup, pageSize: 5 } }),
    enabled: open && kind === 'device' && !prefilledUnit && lookup.length >= 6,
  })
  const unit = prefilledUnit ? null : (found?.items.find((u) => u.imei === lookup) ?? null)

  // Pre-pick the vendor that the free-text name on the purchase line points at.
  const vendorList = vendors?.items ?? []
  const wantedName = (prefill?.vendorName ?? unit?.vendor ?? '').trim().toLowerCase()
  const matchedVendor = wantedName
    ? vendorList.find((v) => v.name.trim().toLowerCase() === wantedName)
    : undefined
  const resolvedVendorId = vendorId || matchedVendor?.id || ''

  const resolvedUnitId = prefilledUnit ?? unit?.id ?? null
  const expectedDefault = prefill?.amountCents ?? unit?.costCents ?? null

  function reset() {
    setMode('device')
    setImei('')
    setProductId('')
    setLocationId('')
    setQty('1')
    setVendorId('')
    setReason('')
    setAmount('')
    setInvoiceRef('')
  }

  function handleClose() {
    reset()
    onClose()
  }

  const create = useMutation({
    mutationFn: () => {
      const amountCents = amount.trim() === '' ? null : dollarsToCents(amount)
      const common = {
        vendorId: resolvedVendorId,
        reason: reason.trim(),
        sourceCostId: prefill?.sourceCostId ?? null,
        invoiceRef: invoiceRef.trim() || prefill?.invoiceRef || null,
        expectedCreditCents: amountCents,
      }
      return kind === 'device'
        ? api.post<VendorReturn>('/vendor-returns', { ...common, unitId: resolvedUnitId })
        : api.post<VendorReturn>('/vendor-returns', {
            ...common,
            productId: productId || prefill?.productId,
            locationId,
            qty: Number.parseInt(qty, 10),
          })
    },
    onSuccess: async () => {
      await invalidate()
      toast.success('Marked as needing to go back to the vendor')
      handleClose()
    },
    onError: errorToast(toast, 'Could not start the return'),
  })

  const hasTarget =
    kind === 'device'
      ? Boolean(resolvedUnitId)
      : Boolean(productId || prefill?.productId) &&
        Boolean(locationId) &&
        Number.parseInt(qty, 10) > 0
  const valid = hasTarget && Boolean(resolvedVendorId) && reason.trim() !== ''

  return (
    <Dialog open={open} onOpenChange={(next) => !next && handleClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Needs to be returned</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <p className="text-sm text-foreground-muted">
            The item comes out of sellable stock right away. Track it through shipped back and
            received by the vendor, then record the refund.
          </p>

          {prefill?.unitId ? (
            <p className="text-sm">
              <span className="font-identifier">{prefill.imei}</span>
              {prefill.productName ? ` · ${prefill.productName}` : ''}
            </p>
          ) : prefill?.productId ? (
            <>
              <p className="text-sm">{prefill.productName}</p>
              <div className="grid grid-cols-[minmax(0,3fr)_6rem] gap-3">
                <Field label="Taken from" required>
                  {(props) => (
                    <Select value={locationId} onValueChange={setLocationId}>
                      <SelectTrigger {...props}>
                        <SelectValue placeholder="Choose…" />
                      </SelectTrigger>
                      <SelectContent>
                        {(locations ?? [])
                          .filter((l) => l.isActive)
                          .map((l) => (
                            <SelectItem key={l.id} value={l.id}>
                              {l.name}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  )}
                </Field>
                <Field label="Qty" required>
                  {(props) => (
                    <Input
                      {...props}
                      type="number"
                      min={1}
                      value={qty}
                      onChange={(e) => setQty(e.target.value)}
                    />
                  )}
                </Field>
              </div>
            </>
          ) : (
            <>
              <Field label="What is being returned">
                {(props) => (
                  <Select value={mode} onValueChange={(v) => setMode(v as typeof mode)}>
                    <SelectTrigger {...props}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="device">A phone or SIM (by IMEI / ICCID)</SelectItem>
                      <SelectItem value="stock">Counted stock (accessories, parts)</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </Field>
              {kind === 'device' ? (
                <Field
                  label="IMEI or ICCID"
                  required
                  hint={
                    unit
                      ? `${unit.productName} · ${humanizeStatus(unit.status)}`
                      : lookup.length >= 6
                        ? 'No device with that number.'
                        : 'Scan or type the number.'
                  }
                >
                  {(props) => (
                    <Input
                      {...props}
                      className="font-identifier"
                      value={imei}
                      onChange={(e) => setImei(e.target.value)}
                      autoFocus
                    />
                  )}
                </Field>
              ) : (
                <div className="grid grid-cols-[minmax(0,2fr)_minmax(0,2fr)_6rem] gap-3">
                  <Field label="Product" required>
                    {(props) => (
                      <Select value={productId} onValueChange={setProductId}>
                        <SelectTrigger {...props}>
                          <SelectValue placeholder="Choose…" />
                        </SelectTrigger>
                        <SelectContent>
                          {stockProducts?.items.map((p) => (
                            <SelectItem key={p.id} value={p.id}>
                              {p.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </Field>
                  <Field label="Taken from" required>
                    {(props) => (
                      <Select value={locationId} onValueChange={setLocationId}>
                        <SelectTrigger {...props}>
                          <SelectValue placeholder="Choose…" />
                        </SelectTrigger>
                        <SelectContent>
                          {(locations ?? [])
                            .filter((l) => l.isActive)
                            .map((l) => (
                              <SelectItem key={l.id} value={l.id}>
                                {l.name}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                    )}
                  </Field>
                  <Field label="Qty" required>
                    {(props) => (
                      <Input
                        {...props}
                        type="number"
                        min={1}
                        value={qty}
                        onChange={(e) => setQty(e.target.value)}
                      />
                    )}
                  </Field>
                </div>
              )}
            </>
          )}

          <Field label="Vendor" required>
            {(props) => (
              <Select value={resolvedVendorId} onValueChange={setVendorId}>
                <SelectTrigger {...props}>
                  <SelectValue placeholder="Choose the vendor…" />
                </SelectTrigger>
                <SelectContent>
                  {vendorList.map((v) => (
                    <SelectItem key={v.id} value={v.id}>
                      {v.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </Field>
          <Field label="What is wrong with it" required>
            {(props) => (
              <Textarea
                {...props}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={2}
                placeholder="Cracked screen, dead on arrival…"
              />
            )}
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field
              label="Expected refund"
              hint={
                expectedDefault !== null
                  ? `Blank uses the cost: ${formatMoney(expectedDefault)}`
                  : 'Blank uses the item’s recorded cost.'
              }
            >
              {(props) => (
                <Input
                  {...props}
                  type="number"
                  min={0}
                  step="0.01"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                />
              )}
            </Field>
            <Field label="Invoice reference">
              {(props) => (
                <Input
                  {...props}
                  value={invoiceRef}
                  placeholder={prefill?.invoiceRef ?? unit?.purchaseOrderRef ?? ''}
                  onChange={(e) => setInvoiceRef(e.target.value)}
                />
              )}
            </Field>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={create.isPending}
            disabled={!valid}
            onClick={() => create.mutate()}
          >
            Mark as needing return
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ------------------------------------------------------- step a return along

type StepDialog = { kind: 'ship' | 'credit'; ret: VendorReturn } | null

function ShipDialog({ ret, onClose }: { ret: VendorReturn | null; onClose: () => void }) {
  const toast = useToast()
  const invalidate = useInvalidateReturns()
  const [tracking, setTracking] = useState('')

  const ship = useMutation({
    mutationFn: () =>
      api.post(`/vendor-returns/${ret?.id}/ship`, {
        trackingNumber: tracking.trim() || null,
        note: null,
      }),
    onSuccess: async () => {
      await invalidate()
      toast.success('Marked as shipped back')
      setTracking('')
      onClose()
    },
    onError: errorToast(toast, 'Could not update the return'),
  })

  return (
    <Dialog open={ret !== null} onOpenChange={(next) => !next && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Shipped back to {ret?.vendorName}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <Field label="Tracking number" hint="Optional.">
            {(props) => (
              <Input
                {...props}
                value={tracking}
                onChange={(e) => setTracking(e.target.value)}
                autoFocus
              />
            )}
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" loading={ship.isPending} onClick={() => ship.mutate()}>
            Mark shipped
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function CreditDialog({ ret, onClose }: { ret: VendorReturn | null; onClose: () => void }) {
  const toast = useToast()
  const invalidate = useInvalidateReturns()
  const [outcome, setOutcome] = useState<'account_credit' | 'refund'>('account_credit')
  const [amount, setAmount] = useState('')
  const [note, setNote] = useState('')

  const amountValue = amount === '' && ret ? centsToDollars(ret.expectedCreditCents) : amount
  const cents = dollarsToCents(amountValue)

  const credit = useMutation({
    mutationFn: () =>
      api.post(`/vendor-returns/${ret?.id}/credit`, {
        outcome,
        amountCents: cents,
        note: note.trim() || null,
      }),
    onSuccess: async () => {
      await invalidate()
      toast.success(outcome === 'account_credit' ? 'Credit added to the vendor' : 'Refund recorded')
      setAmount('')
      setNote('')
      onClose()
    },
    onError: errorToast(toast, 'Could not record the refund'),
  })

  const short = ret ? ret.expectedCreditCents - cents : 0

  return (
    <Dialog open={ret !== null} onOpenChange={(next) => !next && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Vendor refund — {ret?.vendorName}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <Field label="How did they refund it?">
            {(props) => (
              <Select value={outcome} onValueChange={(v) => setOutcome(v as typeof outcome)}>
                <SelectTrigger {...props}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="account_credit">
                    Credit on their account (use on a later invoice)
                  </SelectItem>
                  <SelectItem value="refund">Money came back (no account credit)</SelectItem>
                </SelectContent>
              </Select>
            )}
          </Field>
          <Field
            label="Amount they refunded"
            hint={
              short > 0
                ? `${formatMoney(short)} less than the ${formatMoney(ret?.expectedCreditCents ?? 0)} expected.`
                : `Expected ${formatMoney(ret?.expectedCreditCents ?? 0)}.`
            }
          >
            {(props) => (
              <Input
                {...props}
                type="number"
                min={0}
                step="0.01"
                value={amountValue}
                onChange={(e) => setAmount(e.target.value)}
              />
            )}
          </Field>
          <Field label="Note" hint="Optional — e.g. restocking fee kept.">
            {(props) => <Input {...props} value={note} onChange={(e) => setNote(e.target.value)} />}
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={credit.isPending}
            disabled={outcome === 'account_credit' && cents <= 0}
            onClick={() => credit.mutate()}
          >
            Record refund
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ------------------------------------------------------------- Returns tab

export function ReturnsTab({ canWrite }: { canWrite: boolean }) {
  const toast = useToast()
  const invalidate = useInvalidateReturns()
  const [statusFilter, setStatusFilter] = useState<VendorReturnStatus | 'open' | 'all'>('open')
  const [q, setQ] = useState('')
  const debouncedQ = useDebouncedValue(q, 250)
  const [starting, setStarting] = useState(false)
  const [step, setStep] = useState<StepDialog>(null)

  const filters = {
    pageSize: 50,
    q: debouncedQ || undefined,
    open: statusFilter === 'open' ? 'true' : undefined,
    status: statusFilter === 'open' || statusFilter === 'all' ? undefined : statusFilter,
  }

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: queryKeys.vendorReturns.list(filters),
    queryFn: ({ pageParam }) =>
      api.get<{ items: VendorReturn[]; total: number; page: number; pageSize: number }>(
        '/vendor-returns',
        { query: { ...filters, page: pageParam } },
      ),
    initialPageParam: 1,
    getNextPageParam: (last) =>
      last.page * last.pageSize < last.total ? last.page + 1 : undefined,
  })
  const rows = data?.pages.flatMap((p) => p.items) ?? []
  const total = data?.pages[0]?.total ?? 0

  const advance = useMutation({
    mutationFn: (vars: { id: string; action: 'vendor-received' | 'cancel' }) =>
      api.post(`/vendor-returns/${vars.id}/${vars.action}`),
    onSuccess: async (_data, vars) => {
      await invalidate()
      toast.success(vars.action === 'cancel' ? 'Return cancelled — back in stock' : 'Updated')
    },
    onError: errorToast(toast, 'Could not update the return'),
  })

  const columns = useMemo<ColumnDef<VendorReturn, unknown>[]>(
    () => [
      {
        header: 'Item',
        id: 'item',
        cell: ({ row }) => (
          <div>
            {row.original.productName}
            {row.original.qty > 1 ? ` × ${row.original.qty}` : ''}
            <div className="text-xs text-foreground-muted">
              {row.original.unitImei ? (
                <span className="font-identifier">{row.original.unitImei}</span>
              ) : (
                (row.original.locationName ?? '—')
              )}
            </div>
          </div>
        ),
      },
      {
        header: 'Vendor',
        id: 'vendor',
        cell: ({ row }) => (
          <div>
            {row.original.vendorName}
            <div className="text-xs text-foreground-muted">{row.original.invoiceRef ?? '—'}</div>
          </div>
        ),
      },
      {
        header: 'Problem',
        accessorKey: 'reason',
        cell: ({ row }) => <span className="line-clamp-2 max-w-64">{row.original.reason}</span>,
      },
      {
        header: 'Status',
        id: 'status',
        cell: ({ row }) => (
          <div>
            <ReturnStatusBadge status={row.original.status} />
            {row.original.trackingNumber ? (
              <div className="mt-0.5 text-xs text-foreground-muted">
                {row.original.trackingNumber}
              </div>
            ) : null}
          </div>
        ),
      },
      {
        header: 'Refund',
        id: 'refund',
        cell: ({ row }) => {
          const r = row.original
          if (r.status === 'credited' && r.creditedCents !== null) {
            return (
              <div className="tabular">
                {formatMoney(r.creditedCents)}
                <div className="text-xs text-foreground-muted">
                  {r.outcome === 'account_credit' ? 'On account' : 'Money back'}
                  {r.creditedCents < r.expectedCreditCents
                    ? ` · ${formatMoney(r.expectedCreditCents - r.creditedCents)} short`
                    : ''}
                </div>
              </div>
            )
          }
          return (
            <span className="tabular text-foreground-muted">
              {formatMoney(r.expectedCreditCents)} expected
            </span>
          )
        },
      },
      ...(canWrite
        ? [
            {
              header: '',
              id: 'actions',
              cell: ({ row }: { row: { original: VendorReturn } }) => {
                const r = row.original
                return (
                  <div className="flex items-center gap-1">
                    {r.status === 'needs_return' ? (
                      <>
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => setStep({ kind: 'ship', ret: r })}
                        >
                          Mark shipped
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          loading={advance.isPending}
                          onClick={() => advance.mutate({ id: r.id, action: 'cancel' })}
                        >
                          Cancel
                        </Button>
                      </>
                    ) : null}
                    {r.status === 'shipped' ? (
                      <Button
                        size="sm"
                        variant="secondary"
                        loading={advance.isPending}
                        onClick={() => advance.mutate({ id: r.id, action: 'vendor-received' })}
                      >
                        Vendor received
                      </Button>
                    ) : null}
                    {r.status === 'received_by_vendor' ? (
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => setStep({ kind: 'credit', ret: r })}
                      >
                        Record refund
                      </Button>
                    ) : null}
                  </div>
                )
              },
            } satisfies ColumnDef<VendorReturn, unknown>,
          ]
        : []),
    ],
    [canWrite, advance],
  )

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <Field label="Status">
          {(props) => (
            <Select
              value={statusFilter}
              onValueChange={(v) => setStatusFilter(v as typeof statusFilter)}
            >
              <SelectTrigger {...props} className="w-44">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="open">In progress</SelectItem>
                <SelectItem value="all">Everything</SelectItem>
                {VENDOR_RETURN_STATUSES.map((s) => (
                  <SelectItem key={s} value={s}>
                    {RETURN_STATUS_LABEL[s]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </Field>
        <Field label="Search">
          {(props) => (
            <Input
              {...props}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="IMEI, product, vendor, invoice…"
            />
          )}
        </Field>
        {canWrite ? (
          <Button
            variant="secondary"
            size="sm"
            className="ms-auto"
            onClick={() => setStarting(true)}
          >
            <Plus /> Return an item
          </Button>
        ) : null}
      </div>

      <DataTable
        columns={columns}
        data={rows}
        isLoading={isLoading}
        infinite={{
          total,
          hasMore: Boolean(hasNextPage),
          isFetchingMore: isFetchingNextPage,
          onLoadMore: () => void fetchNextPage(),
        }}
        empty={
          <EmptyState
            icon={PackageX}
            title="Nothing being returned"
            description="Damaged items you send back to a vendor are tracked here, from needing a return to the refund."
          />
        }
      />

      <StartReturnDialog open={starting} prefill={null} onClose={() => setStarting(false)} />
      <ShipDialog ret={step?.kind === 'ship' ? step.ret : null} onClose={() => setStep(null)} />
      <CreditDialog ret={step?.kind === 'credit' ? step.ret : null} onClose={() => setStep(null)} />
    </div>
  )
}

// --------------------------------------------------------------- invoice view

/** One supplier invoice after returns: its lines, what was sent back, credit applied, and what is still owed. */
export function InvoiceDialog({
  target,
  onClose,
}: {
  target: { vendor: string; invoiceRef: string } | null
  onClose: () => void
}) {
  const { data, isLoading } = useQuery({
    queryKey: queryKeys.vendorReturns.invoice(target?.vendor ?? '', target?.invoiceRef ?? ''),
    queryFn: () =>
      api.get<InvoiceSummary>('/invoices/summary', {
        query: { vendor: target?.vendor, invoiceRef: target?.invoiceRef },
      }),
    enabled: target !== null,
  })

  return (
    <Dialog open={target !== null} onOpenChange={(next) => !next && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            Invoice {target?.invoiceRef} · {target?.vendor}
          </DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          {isLoading || !data ? (
            <Skeleton className="h-40 w-full" />
          ) : (
            <>
              <ul className="divide-y divide-border rounded-md border border-border text-sm">
                {data.lines.map((line) => (
                  <li
                    key={line.costId}
                    className="flex items-center justify-between gap-3 px-3 py-2"
                  >
                    <div className={line.returnStatus ? 'text-foreground-muted line-through' : ''}>
                      {line.label}
                      {line.unitImei ? (
                        <span className="font-identifier ms-2 text-xs">{line.unitImei}</span>
                      ) : null}
                    </div>
                    <div className="flex items-center gap-2">
                      {line.returnStatus ? <ReturnStatusBadge status={line.returnStatus} /> : null}
                      <span className="tabular">{formatMoney(line.amountCents)}</span>
                    </div>
                  </li>
                ))}
                {data.lines.length === 0 ? (
                  <li className="px-3 py-2 text-foreground-muted">No lines on this invoice.</li>
                ) : null}
              </ul>

              <dl className="space-y-1 text-sm">
                <SummaryRow label="Invoice total" cents={data.totalCents} />
                <SummaryRow label="Already paid" cents={-data.paidCents} />
                <SummaryRow
                  label="Returned goods (refund due / received)"
                  cents={-data.returnedCents}
                />
                <SummaryRow label="Vendor credit applied" cents={-data.creditAppliedCents} />
                <div className="flex justify-between border-t border-border pt-2 font-medium">
                  <dt>Still owed</dt>
                  <dd className="tabular">{formatMoney(data.netOwedCents)}</dd>
                </div>
              </dl>
              {data.returns.some((r) => r.outcome === 'account_credit') ? (
                <p className="text-xs text-foreground-muted">
                  Returns refunded as account credit don’t reduce this invoice until that credit is
                  applied to it from the vendor’s page.
                </p>
              ) : null}
            </>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function SummaryRow({ label, cents }: { label: string; cents: number }) {
  return (
    <div className="flex justify-between text-foreground-muted">
      <dt>{label}</dt>
      <dd className="tabular">{cents === 0 ? '—' : formatMoney(cents)}</dd>
    </div>
  )
}

// ---------------------------------------------------------------- vendor page

function ApplyCreditDialog({
  vendorId,
  balanceCents,
  open,
  onClose,
}: {
  vendorId: string
  balanceCents: number
  open: boolean
  onClose: () => void
}) {
  const toast = useToast()
  const invalidate = useInvalidateReturns()
  const [amount, setAmount] = useState('')
  const [invoiceRef, setInvoiceRef] = useState('')

  const cents = dollarsToCents(amount)
  const apply = useMutation({
    mutationFn: () =>
      api.post(`/vendors/${vendorId}/credit/apply`, {
        amountCents: cents,
        invoiceRef: invoiceRef.trim(),
        note: null,
      }),
    onSuccess: async () => {
      await invalidate()
      toast.success('Credit applied to the invoice')
      setAmount('')
      setInvoiceRef('')
      onClose()
    },
    onError: errorToast(toast, 'Could not apply the credit'),
  })

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Apply credit to an invoice</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <Field label="Invoice reference" required>
            {(props) => (
              <Input
                {...props}
                value={invoiceRef}
                onChange={(e) => setInvoiceRef(e.target.value)}
                autoFocus
              />
            )}
          </Field>
          <Field label="Amount" required hint={`${formatMoney(balanceCents)} available.`}>
            {(props) => (
              <Input
                {...props}
                type="number"
                min={0}
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
            )}
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={apply.isPending}
            disabled={cents <= 0 || cents > balanceCents || invoiceRef.trim() === ''}
            onClick={() => apply.mutate()}
          >
            Apply credit
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** The vendor's credit balance and history, plus their returns still in progress. */
export function VendorCreditCard({ vendorId, canWrite }: { vendorId: string; canWrite: boolean }) {
  const [applying, setApplying] = useState(false)

  const { data: credit, isLoading } = useQuery({
    queryKey: queryKeys.vendors.credit(vendorId),
    queryFn: () => api.get<VendorCredit>(`/vendors/${vendorId}/credit`),
  })
  const { data: open } = useQuery({
    queryKey: queryKeys.vendorReturns.list({ vendorId, open: 'true', pageSize: 20 }),
    queryFn: () =>
      api.get<{ items: VendorReturn[] }>('/vendor-returns', {
        query: { vendorId, open: 'true', pageSize: 20 },
      }),
  })

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between">
        <CardTitle>Credit &amp; returns</CardTitle>
        {canWrite && credit && credit.balanceCents > 0 ? (
          <Button size="sm" variant="secondary" onClick={() => setApplying(true)}>
            Apply to invoice
          </Button>
        ) : null}
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading || !credit ? (
          <Skeleton className="h-16 w-full" />
        ) : (
          <>
            <div>
              <div className="text-2xs font-medium uppercase tracking-wide text-foreground-muted">
                Credit with this vendor
              </div>
              <div className="tabular text-2xl font-semibold">
                {formatMoney(credit.balanceCents)}
              </div>
            </div>

            {credit.transactions.length > 0 ? (
              <ul className="divide-y divide-border text-sm">
                {credit.transactions.map((t) => (
                  <li key={t.id} className="flex items-start justify-between gap-3 py-2">
                    <div>
                      {t.kind === 'return_credit'
                        ? 'Credit from a return'
                        : t.kind === 'applied'
                          ? `Applied to invoice ${t.invoiceRef ?? ''}`
                          : 'Adjustment'}
                      <div className="text-xs text-foreground-muted">
                        {new Date(t.createdAt).toLocaleDateString()}
                        {t.note ? ` · ${t.note}` : ''}
                      </div>
                    </div>
                    <div className="tabular text-end">
                      {t.amountCents > 0 ? '+' : '−'}
                      {formatMoney(Math.abs(t.amountCents))}
                      <div className="text-xs text-foreground-muted">
                        {formatMoney(t.balanceAfterCents)}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-foreground-muted">No credit yet.</p>
            )}
          </>
        )}

        {open && open.items.length > 0 ? (
          <div>
            <div className="mb-1 text-2xs font-medium uppercase tracking-wide text-foreground-muted">
              Being returned ({open.items.length})
            </div>
            <ul className="space-y-1 text-sm">
              {open.items.map((r) => (
                <li key={r.id} className="flex items-center justify-between gap-2">
                  <span>
                    {r.productName}
                    {r.unitImei ? (
                      <span className="font-identifier ms-2 text-xs">{r.unitImei}</span>
                    ) : null}
                  </span>
                  <ReturnStatusBadge status={r.status} />
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </CardContent>

      <ApplyCreditDialog
        vendorId={vendorId}
        balanceCents={credit?.balanceCents ?? 0}
        open={applying}
        onClose={() => setApplying(false)}
      />
    </Card>
  )
}
